import axios from 'axios';
import { toast } from 'sonner';

// Step 7.1.2: Hard Web Readiness - Use process.env for standard configuration
const BACKEND_URL = process.env.REACT_APP_API_URL;
const isProduction = process.env.NODE_ENV === 'production';

let apiBase = '';

if (BACKEND_URL) {
    // If explicit URL provided, use it (ensures VITE_API_URL or REACT_APP_API_URL is source of truth)
    apiBase = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;
} else {
    // Default to relative path for Docker/Nginx reverse proxy scenarios
    apiBase = '/api';
}

export const API_BASE = apiBase;

if (isProduction && !BACKEND_URL && window.location.protocol === 'https:') {
    // Audit log for production environment parity
    console.debug("[EndoCore] Production target: Relaying via local reverse proxy (/api)");
}

// finding D-13: `axios.defaults.withCredentials = true` mutated the GLOBAL
// axios instance — any other code in the bundle using bare `axios` (not this
// apiClient instance) picked up cross-origin credentials it never asked for.
// The instance-level `withCredentials: true` below already covers what this
// app actually needs.
const apiClient = axios.create({
    baseURL: API_BASE,
    withCredentials: true
});

// --- GLOBAL ERROR HANDLING & TOASTS ---

// Memory cache for deduplicating identical errors within a 2.5s window
const errorToastCache = new Set();
const DEDUP_WINDOW_MS = 2500;

/**
 * Normalizes an Axios error into a standard structure.
 */
function normalizeApiError(err) {
    const status = err.response?.status || null;
    const data = err.response?.data || {};
    const request_id = err.response?.headers?.['x-request-id'] || null;

    let message = 'An unexpected error occurred';
    let detail = null;
    let code = null;

    // Standardize HTTP statuses to friendly UI messages
    if (status === 401) {
        // Only show "expired" if we actually had a token that is now rejected.
        // Guests shouldn't see an "expired" toast just for being guests.
        message = localStorage.getItem('auth_token') 
            ? "Session expired. Please log in again." 
            : null;
    }
    else if (status === 402) message = "Billing issue: Please update your payment method to continue.";
    else if (status === 403) message = "You don't have access to do that.";
    else if (status === 429) message = "Too many requests. Try again in a minute.";
    else if (status >= 500) message = "Server error. Please try again.";
    else if (!err.response && err.request) message = "Network error. Please check your connection.";

    // Parse backend operational payloads (legacy string 'detail' or structured dict)
    if (data.detail) {
        if (typeof data.detail === 'object') {
            detail = data.detail.user_message || data.detail.message || JSON.stringify(data.detail);
            code = data.detail.code || null;
            // Only override default message if the backend specifically provided a user-message, OR it's a 400 Bad Request
            if (status === 400 || data.detail.user_message) message = detail;
        } else {
            detail = data.detail;
            if (status === 400) message = detail;
        }
    }

    return { status, code, message, request_id, detail, error: err };
}

// --- GLOBAL REQUEST INTERCEPTOR (Diagnostic Token Injection) ---
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Global Interceptor Integration
apiClient.interceptors.response.use(
    (response) => {
        // Pass-through successful responses (No toasts on success)
        return response;
    },
    (error) => {
        const config = error.config || {};

        // Skip toast if component explicitly opted out
        if (config.silent) {
            return Promise.reject(error);
        }

        const normalized = normalizeApiError(error);

        // finding D-12: a 401 used to only show a toast — the token was never
        // cleared and nothing redirected, so an expired/revoked session left
        // the UI looking authenticated (stale user state still in memory)
        // while every subsequent request kept silently 401ing. Only act when
        // a token actually existed (a genuine session-expiry), not for a
        // guest's failed login attempt — same distinction normalizeApiError
        // already makes above for the toast message itself. The /auth/me
        // startup probe passes `{ silent: true }` deliberately and returns
        // before this point, so it never triggers this redirect either.
        if (normalized.status === 401 && localStorage.getItem('auth_token')) {
            setAuthToken(null);
            if (!window.location.pathname.startsWith('/auth/')) {
                window.location.href = '/auth/login';
            }
        }

        // Create a unique signature for this specific error to prevent spam
        const errorSignature = `${normalized.status}:${normalized.message}`;

        if (!errorToastCache.has(errorSignature)) {
            // Register execution in cache
            errorToastCache.add(errorSignature);

            // Set expire timer to release the block after window passes
            setTimeout(() => {
                errorToastCache.delete(errorSignature);
            }, DEDUP_WINDOW_MS);

            // Execute Toast
            if (normalized.message) {
                toast.error(normalized.message, {
                    description: (!isProduction && normalized.detail && normalized.detail !== normalized.message)
                        ? normalized.detail
                        : undefined,
                });
            }
        }

        return Promise.reject(error);
    }
);

// --- AUTHENTICATION ---

/**
 * setAuthToken: Atomic localStorage management.
 * The request interceptor dynamically reads from here on every call.
 */
export const setAuthToken = (token) => {
    if (token) {
        localStorage.setItem('auth_token', token);
    } else {
        localStorage.removeItem('auth_token');
    }
};

export default apiClient;
